import { Box3, BoxGeometry, Group, Mesh, MeshBasicMaterial, type Object3D, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { ModelAsset } from "../models/model-assets";
import { createRackSelectionHighlight } from "./rack-shelf";

export const defaultSingleStationReciprocatingShuttleHeight = 0.4;

export type SingleStationReciprocatingShuttleOptions = {
  height?: number;
  length?: number;
  position?: { x?: number; y?: number; z?: number };
  rotationY?: number;
};

type AlignedPart = {
  content: Group;
  object: Group;
  size: Vector3;
};

const defaultOptions = {
  height: defaultSingleStationReciprocatingShuttleHeight,
  length: 10,
  rotationY: 0
};
const selectionHighlightPadding = 0.06;
const partRotationY = Math.PI / 2;
const topYOffset = -0.16;

export class SingleStationReciprocatingShuttle extends Group {
  private body?: AlignedPart;
  private height: number;
  private readonly hitTarget = createHitTarget();
  private readonly length: number;
  private readonly selectionHighlight = createRackSelectionHighlight();
  private selected = false;
  private readonly structure = new Group();
  private top?: AlignedPart;
  private track?: AlignedPart;

  constructor({
    height = defaultOptions.height,
    length = defaultOptions.length,
    position = {},
    rotationY = defaultOptions.rotationY
  }: SingleStationReciprocatingShuttleOptions = {}) {
    super();
    this.height = validatePositiveNumber(height, "height");
    this.length = validatePositiveNumber(length, "length");
    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.name = "single-station-reciprocating-shuttle";
    this.structure.name = "single-station-reciprocating-shuttle-structure";
    this.hitTarget.name = "single-station-reciprocating-shuttle-hit-target";
    this.selectionHighlight.name = "single-station-reciprocating-shuttle-selection-highlight";
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
    this.rotation.y = rotationY;
    this.add(this.structure, this.hitTarget, this.selectionHighlight);
  }

  static async create(
    options: SingleStationReciprocatingShuttleOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const shuttle = new SingleStationReciprocatingShuttle(options);
    await shuttle.loadStructure(loader);
    return shuttle;
  }

  getHeight() {
    return this.height;
  }

  getHitTargets(): Object3D[] {
    return [this.hitTarget];
  }

  setHeight(height: number) {
    this.height = validatePositiveNumber(height, "height");
    this.layoutStructure();
  }

  setSelected(selected: boolean) {
    this.selected = selected;
    this.selectionHighlight.visible = selected;
  }

  private async loadStructure(loader: ModelAssetLoader) {
    const [track, body, top] = await Promise.all([
      loadBottomAlignedPart(
        loader,
        ModelAsset.SingleStationReciprocatingShuttleRail,
        "single-station-reciprocating-shuttle-track"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.SingleStationReciprocatingShuttleBody,
        "single-station-reciprocating-shuttle-body"
      ),
      loadBottomAlignedPart(
        loader,
        ModelAsset.SingleStationReciprocatingShuttleTop,
        "single-station-reciprocating-shuttle-top"
      )
    ]);

    this.track = track;
    this.body = body;
    this.top = top;
    [track, body, top].forEach(({ content }) => {
      content.rotation.y = partRotationY;
    });
    this.structure.add(track.object, body.object, top.object);
    this.layoutStructure();
  }

  private layoutStructure() {
    if (!this.track || !this.body || !this.top) {
      return;
    }

    this.track.content.scale.z = this.length / this.track.size.z;
    this.body.content.scale.y = this.height / this.body.size.y;
    this.body.object.position.y = this.track.size.y;
    const bodyTopY = this.track.size.y + this.height;
    const topBaseY = bodyTopY + topYOffset;
    this.top.object.position.y = topBaseY;

    const minimumY = Math.min(0, topBaseY);
    const maximumY = Math.max(bodyTopY, topBaseY + this.top.size.y);
    const totalHeight = maximumY - minimumY;
    const totalLength = Math.max(this.length, this.body.size.z, this.top.size.z);
    const totalWidth = Math.max(this.track.size.x, this.body.size.x, this.top.size.x);
    this.hitTarget.position.y = minimumY + totalHeight / 2;
    this.hitTarget.scale.set(totalLength, totalHeight, totalWidth);
    this.selectionHighlight.position.y = minimumY + totalHeight / 2;
    this.selectionHighlight.scale.set(
      totalLength + selectionHighlightPadding * 2,
      totalHeight + selectionHighlightPadding * 2,
      totalWidth + selectionHighlightPadding * 2
    );
    this.selectionHighlight.visible = this.selected;
  }
}

function createHitTarget() {
  const material = new MeshBasicMaterial({
    depthWrite: false,
    opacity: 0,
    transparent: true
  });
  material.colorWrite = false;
  return new Mesh(new BoxGeometry(1, 1, 1), material);
}

async function loadBottomAlignedPart(
  loader: ModelAssetLoader,
  asset: ModelAsset,
  name: string
): Promise<AlignedPart> {
  const model = await loader.load(asset);
  model.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(model);
  if (bounds.isEmpty()) {
    throw new Error(`${asset} model has no renderable geometry`);
  }

  const center = bounds.getCenter(new Vector3());
  const size = bounds.getSize(new Vector3());
  const object = new Group();
  const content = new Group();
  model.position.set(
    model.position.x - center.x,
    model.position.y - bounds.min.y,
    model.position.z - center.z
  );
  object.name = name;
  content.add(model);
  object.add(content);
  return { content, object, size };
}

function validatePositiveNumber(value: number, label: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${label} must be a positive finite number`);
  }
  return value;
}
